import { ChangeEvent, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, X } from 'lucide-react';
import { Button } from '@/components/ui-kit/button';
import { Label } from '@/components/ui-kit/label';
import { OptionalMark } from '@/components/ui-kit/optional-mark';
import { useToast } from '@/hooks/use-toast';
import { validateImageFile } from '../../utils/id-card-rules';

interface ImageUploadFieldProps {
  label: string;
  /** Existing UILM key for the "click to select" prompt. */
  promptKey: string;
  hint?: string;
  /** A file the user just picked. */
  file: File | null;
  /** URL of the already-saved file, when editing an application. */
  existingUrl?: string;
  /** Existing UILM key for the label of the already-saved file link. */
  existingLabelKey: string;
  onFileChange: (file: File | null) => void;
  onRemoveExisting: () => void;
  error?: string;
  /** Marks the label with `*`. */
  required?: boolean;
}

/** Image-only, 5 MB-limited picker for the ID card photo and payment receipt. */
export const ImageUploadField = ({
  label,
  promptKey,
  hint,
  file,
  existingUrl,
  existingLabelKey,
  onFileChange,
  onRemoveExisting,
  error,
  required,
}: ImageUploadFieldProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const inputId = useId();

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const picked = event.target.files?.[0];
    event.target.value = '';
    if (!picked) return;

    const problem = validateImageFile(picked);
    if (problem) {
      toast({ variant: 'destructive', title: t(problem) });
      return;
    }
    onFileChange(picked);
  };

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={inputId}>
        {label}
        {!required && <OptionalMark />}
      </Label>

      {!file && !existingUrl && (
        <label
          htmlFor={inputId}
          className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed p-4 text-sm text-muted-foreground hover:bg-muted"
        >
          <Upload className="size-4" />
          {t(promptKey)}
        </label>
      )}
      <input
        id={inputId}
        type="file"
        accept="image/*"
        className="sr-only"
        onChange={handleChange}
      />

      {file && (
        <div className="flex items-center justify-between rounded-md border p-2 text-sm">
          <span className="truncate">{file.name}</span>
          <Button type="button" variant="ghost" size="icon" onClick={() => onFileChange(null)}>
            <X className="size-4" />
          </Button>
        </div>
      )}
      {!file && existingUrl && (
        <div className="flex items-center justify-between rounded-md border p-2 text-sm">
          <a href={existingUrl} target="_blank" rel="noreferrer" className="text-primary underline">
            {t(existingLabelKey)}
          </a>
          <Button type="button" variant="ghost" size="icon" onClick={onRemoveExisting}>
            <X className="size-4" />
          </Button>
        </div>
      )}

      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      {error && <p className="text-sm font-medium text-destructive">{error}</p>}
    </div>
  );
};
